import { ChevronDown } from "lucide-react";
import { useId } from "react";
import type { ReactNode } from "react";
import { useSidebarCollapsed, useSidebarStore } from "./sidebar-store";

interface SidebarSectionProps {
  label: string;
  children: ReactNode;
  /** Sbalený sidebar — místo labelu jen oddělovací linka. */
  collapsed?: boolean | undefined;
}

/** Label → kebab-case bez diakritiky pro data-testid a id. */
function slugify(label: string): string {
  // Diakritika char-by-char (regex třída s combining marks je misleading —
  // stejný vzor jako normalizeHeader v import-klienti-dialog).
  const bezDiakritiky = Array.from(label.normalize("NFD"))
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code < 0x0300 || code > 0x036f;
    })
    .join("");
  return bezDiakritiky
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Skupina odkazů v sidebaru s horním labelem v kapitálkách. */
export function SidebarSection({ label, children, collapsed: collapsedProp }: SidebarSectionProps) {
  const ctxCollapsed = useSidebarCollapsed();
  const collapsed = collapsedProp ?? ctxCollapsed;
  // Default SBALENO (majitel: „menu ať je při přihlášení zbalené") — rozbalené
  // jsou jen sekce, které si uživatel sám otevřel (persistuje se).
  const sectionCollapsed = useSidebarStore((s) => s.collapsedSections[label] ?? true);
  const toggleSection = useSidebarStore((s) => s.toggleSection);
  // useId MUSÍ být před podmíněným returnem (React #310 — počet hooků se
  // nesmí mezi rendery měnit); label-based ID by navíc kolidovalo mezi
  // desktop a mobilní instancí sidebaru.
  const instanceId = useId();

  // Icon režim sidebaru — sekce se nesbalují, jen oddělovací linka.
  if (collapsed) {
    return (
      <div className="flex flex-col gap-1">
        <div
          aria-hidden="true"
          className="mx-2 mt-4 mb-1 border-[hsl(var(--sidebar-divider))] border-t"
        />
        {children}
      </div>
    );
  }

  const slug = slugify(label);
  const contentId = `sidebar-section-${slug}-${instanceId}`;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => toggleSection(label)}
        aria-expanded={!sectionCollapsed}
        aria-controls={sectionCollapsed ? undefined : contentId}
        className="flex w-full items-center gap-1.5 rounded-md px-3 pt-4 pb-1 text-left font-semibold text-[0.7rem] text-[hsl(var(--sidebar-fg-muted))] uppercase tracking-wider transition-colors hover:text-[hsl(var(--sidebar-fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active-bg))]"
        data-testid={`sidebar-section-toggle-${slug}`}
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-150 ${sectionCollapsed ? "-rotate-90" : ""}`}
          aria-hidden="true"
        />
        <span>{label}</span>
      </button>
      {sectionCollapsed ? null : (
        <div id={contentId} className="flex flex-col gap-1">
          {children}
        </div>
      )}
    </div>
  );
}
